<template>
  <div class="kiosk-step-details">
    <div class="details-container">
      <!-- Form Fields (Fixed, not scrollable) -->
      <div class="form-fields">
        <!-- Name Field -->
        <div 
          class="form-field"
          :class="{ focused: focusedInput === 'name' }"
          @click="setFocus('name')"
        >
          <div class="field-header">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="field-icon"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle
                cx="12"
                cy="7"
                r="4"
              />
            </svg>
            <span class="field-label">Name</span>
          </div>
          <div class="field-value">
            <span
              v-if="form.name"
              class="field-text"
            >{{ form.name }}</span>
            <span
              v-else
              class="field-placeholder"
            >Enter your name</span>
            <span
              v-if="focusedInput === 'name'"
              class="field-cursor"
            >|</span>
          </div>
        </div>

        <!-- Phone Field -->
        <div 
          class="form-field"
          :class="{
            focused: focusedInput === 'phone',
            invalid: phoneError && form.phone.length > 0
          }"
          @click="setFocus('phone')"
        >
          <div class="field-header">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="field-icon"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            <span class="field-label">Phone Number</span>
          </div>
          <div class="field-value phone-value">
            <span class="country-prefix">+1</span>
            <span
              v-if="formattedPhone"
              class="field-text"
            >{{ formattedPhone }}</span>
            <span
              v-else
              class="field-placeholder"
            >555-000-0000</span>
            <span
              v-if="focusedInput === 'phone'"
              class="field-cursor"
            >|</span>
          </div>
          <div
            v-if="phoneError && form.phone.length > 0"
            class="error-hint"
          >
            Please enter a valid 10-digit number
          </div>
        </div>
      </div>

      <!-- Footer Area (Fixed) -->
      <div class="footer-area">
        <div class="keyboard-container">
          <VirtualKeyboard
            :mode="focusedInput === 'phone' ? 'numpad' : 'default'"
            @keyPress="handleKeyPress"
            @backspace="handleBackspace"
            @enter="handleEnter"
          />
        </div>

        <div class="action-buttons">
          <button
            class="confirm-btn"
            :class="{ disabled: isNextDisabled }"
            @click="handleNext"
            :disabled="isNextDisabled"
          >
            <span>{{ buttonText }}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VirtualKeyboard from "@/components/VirtualKeyboard.vue";

export default {
  name: "StepCustomerDetails",
  components: {
    VirtualKeyboard,
  },
  props: {
    initialCustomerData: {
      type: Object,
      default: () => ({ name: "", phone: "" }),
    },
  },
  data() {
    return {
      form: {
        name: this.initialCustomerData.name || "",
        phone: this.initialCustomerData.phone || "",
      },
      countryCode: "+1",
      focusedInput: "name", // 'name' or 'phone'
    };
  },
  computed: {
    currentInputValue() {
      if (this.focusedInput === "phone") {
        return (this.form.phone || "").replace(/\D/g, "");
      }
      return this.form[this.focusedInput] || "";
    },
    formattedPhone() {
      if (!this.form.phone) return '';
      const digits = this.form.phone.replace(/\D/g, '');
      if (digits.length === 0) return '';
      if (digits.length <= 3) return `(${digits}`;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    },
    buttonText() {
      return "Next";
    },
    isNextDisabled() {
      if (this.focusedInput === "name") {
        return !this.form.name;
      }
      return !this.form.name || !this.isValidPhone;
    },
    isValidPhone() {
      const digits = this.form.phone.replace(/\D/g, "");
      return digits.length === 10;
    },
    phoneError() {
      if (this.form.phone.length === 0) return false;
      return !this.isValidPhone;
    },
  },
  methods: {
    setFocus(field) {
      this.focusedInput = field;
    },
    handleKeyPress(key) {
      if (this.focusedInput === "phone") {
        // Limit to 10 digits
        const digits = this.form.phone.replace(/\D/g, "");
        if (digits.length < 10) {
          this.form.phone = digits + key;
        }
      } else {
        this.form.name = (this.form.name || "") + key;
      }

      this.$emit("update", {
        customer: {
          name: this.form.name,
          phone: this.form.phone,
        },
      });
    },
    handleBackspace() {
      if (this.focusedInput === "phone") {
        const digits = this.form.phone.replace(/\D/g, "");
        this.form.phone = digits.slice(0, -1);
      } else {
        this.form.name = (this.form.name || "").slice(0, -1);
      }

      this.$emit("update", {
        customer: {
          name: this.form.name,
          phone: this.form.phone,
        },
      });
    },
    handleEnter() {
      this.handleNext();
    },
    handleNext() {
      if (this.focusedInput === "name") {
        if (this.form.name) {
          this.setFocus("phone");
        }
      } else {
        if (this.form.name && this.isValidPhone) {
          this.confirmDetails();
        }
      }
    },
    confirmDetails() {
      const fullPhone = this.countryCode + this.form.phone.replace(/\D/g, "");
      this.$emit("next", {
        customer: {
          name: this.form.name.trim(),
          phone: fullPhone,
        },
      });
    },
  },
};
</script>

<style scoped>
.kiosk-step-details {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.details-container {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  margin: 0 auto;
  background: #F9FAFB;
  overflow: hidden;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-shrink: 0;
  margin-bottom: 1.5rem;
}

.form-field {
  padding: 1rem;
  border-radius: 12px;
  border: 2px solid #E5E7EB;
  background: #fff;
  transition: all 0.2s;
  cursor: pointer;
}

.form-field.focused {
  border-color: #2563EB;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.form-field.invalid {
  border-color: #DC2626;
}

.form-field.invalid.focused {
  border-color: #DC2626;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.1);
}

.field-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  color: #6B7280;
}

.field-icon {
  width: 16px;
  height: 16px;
  color: #6B7280;
}

.field-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.field-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  min-height: 2rem;
  display: flex;
  align-items: center;
  position: relative;
}

.field-value.phone-value {
  letter-spacing: 0.05em;
}

.field-text {
  color: #111827;
}

.field-placeholder {
  color: #D1D5DB;
  font-style: italic;
  font-weight: 400;
  font-size: 1.125rem;
}

.field-cursor {
  color: #2563EB;
  animation: blink 1s infinite;
  margin-left: 2px;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

.country-prefix {
  font-size: 1.25rem;
  font-weight: 500;
  color: #9CA3AF;
  margin-right: 0.5rem;
  user-select: none;
}

.error-hint {
  color: #DC2626;
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: 0.5rem;
}

/* Footer Area (Keyboard + Button) */
.footer-area {
  flex-shrink: 0;
  margin-top: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.keyboard-container {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  background: #F3F4F6;
  border-radius: 16px;
  padding: 0.5rem;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
}

.action-buttons {
  flex-shrink: 0;
}

.confirm-btn {
  width: 100%;
  padding: 1rem;
  font-weight: 700;
  font-size: 1.25rem;
  border-radius: 12px;
  border: none;
  background: #111827;
  color: white;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  cursor: pointer;
}

.confirm-btn:hover:not(:disabled) {
  background: #1F2937;
  transform: translateY(-1px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.1);
}

.confirm-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.confirm-btn.disabled {
  background: #E5E7EB;
  color: #9CA3AF;
  cursor: not-allowed;
  box-shadow: none;
}
</style>
